import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Github, LayoutDashboard, ShieldCheck, Sparkles, Store } from 'lucide-react';
import { authApi } from '@/api/auth';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const DEMO_ACCOUNTS = [
  {
    id: 'admin',
    name: '管理员',
    email: 'admin@systemhub.com',
    password: 'admin123',
  },
  {
    id: 'tenant',
    name: '租户',
    email: 'ops@systemhub.com',
    password: 'user123',
  },
] as const;

type AuthPanel = 'password' | 'oauth';

function GoogleGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function WeChatGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.31-5.533 3.17-7.14 1.046-.59 2.227-.93 3.48-.93.165 0 .328.01.49.025C14.756 3.562 12.04 2.188 8.691 2.188zm-2.55 5.25c-.65 0-1.178-.528-1.178-1.179 0-.65.528-1.178 1.179-1.178.65 0 1.177.528 1.177 1.178 0 .651-.527 1.179-1.177 1.179zm5.1 0c-.65 0-1.178-.528-1.178-1.179 0-.65.528-1.178 1.178-1.178.651 0 1.179.528 1.179 1.178 0 .651-.528 1.179-1.179 1.179z"
      />
      <path
        fill="currentColor"
        d="M24 14.936c0-3.263-3.212-5.91-7.167-5.91-3.954 0-7.166 2.647-7.166 5.91 0 3.264 3.212 5.911 7.166 5.911.826 0 1.622-.11 2.372-.314a.69.69 0 0 1 .514.082l1.584.926a.228.228 0 0 0 .117.034.227.227 0 0 0 .227-.227c0-.055-.02-.11-.035-.162l-.325-1.233a.49.49 0 0 1 .177-.553c1.477-1.036 2.456-2.558 2.456-4.264zm-9.59-1.423c-.527 0-.954-.428-.954-.955a.953.953 0 1 1 1.907 0c0 .527-.426.955-.953.955zm4.17 0c-.527 0-.954-.428-.954-.955a.953.953 0 1 1 1.907 0c0 .527-.426.955-.953.955z"
      />
    </svg>
  );
}

const LOGIN_SIDE_FEATURES = [
  {
    icon: LayoutDashboard,
    title: '运营与洞察',
    desc: '数据概览、订单与订阅配置',
  },
  {
    icon: Store,
    title: '应用与市场',
    desc: '租户工作台与应用分发',
  },
  {
    icon: ShieldCheck,
    title: '权限与安全',
    desc: '平台 RBAC 与审计就绪',
  },
] as const;

function LoginSideFeatureList({ variant }: { variant: 'desktop' | 'mobile' }) {
  if (variant === 'mobile') {
    return (
      <ul className="mt-6 space-y-2.5 border-t border-white/10 pt-5">
        {LOGIN_SIDE_FEATURES.map(({ icon: Icon, title, desc }) => (
          <li key={title} className="flex gap-3 text-[13px] leading-snug text-zinc-400">
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400/85" aria-hidden />
            <span>
              <span className="font-medium text-zinc-200">{title}</span>
              <span className="text-zinc-500"> · </span>
              {desc}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div
      className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-6 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.07)] backdrop-blur-md"
      role="presentation"
    >
      <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-300/70">
        能力一览
      </p>
      <ul className="space-y-5">
        {LOGIN_SIDE_FEATURES.map(({ icon: Icon, title, desc }) => (
          <li key={title} className="flex gap-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06] text-cyan-300/95 shadow-[0_0_0_1px_rgba(34,211,238,0.12)]">
              <Icon className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0 space-y-0.5 pt-0.5">
              <p className="text-sm font-semibold text-zinc-100">{title}</p>
              <p className="text-sm leading-relaxed text-zinc-400">{desc}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** 左侧整列霓虹背景（铺满容器） */
function NeonColumn({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('relative min-h-full overflow-hidden bg-zinc-950 text-white', className)}>
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.45] motion-reduce:animate-none animate-login-grid-drift"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(34, 211, 238, 0.08) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(34, 211, 238, 0.08) 1px, transparent 1px)
          `,
          backgroundSize: '52px 52px',
        }}
      />
      <div className="pointer-events-none absolute -left-32 top-0 h-[min(100%,32rem)] w-[min(100%,32rem)] rounded-full bg-cyan-500/25 blur-[120px] motion-reduce:animate-none animate-login-neon-pulse" />
      <div className="pointer-events-none absolute -right-24 bottom-0 h-[min(100%,28rem)] w-[min(100%,28rem)] rounded-full bg-fuchsia-500/22 blur-[100px] motion-reduce:animate-none animate-login-neon-pulse [animation-delay:1.2s]" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-zinc-950/20 via-transparent to-zinc-950/80" />
      <div className="pointer-events-none absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent" />
      {children}
    </div>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuthStore.getState();
  const [authPanel, setAuthPanel] = useState<AuthPanel>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirectAfterLogin = (user: { role: string }) => {
    if (user.role === 'admin') {
      navigate('/dashboard');
    } else {
      navigate('/workspace');
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const response = await authApi.login({ email: email.trim(), password });
      const { accessToken, user } = response.data;
      login(user, accessToken);
      redirectAfterLogin(user);
    } catch (err: unknown) {
      console.error('登录失败:', err);
      const data = (err as { response?: { data?: { message?: string } } })?.response
        ?.data;
      setError(
        typeof data?.message === 'string' ? data.message : '登录失败，请重试',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const applyDemo = (account: (typeof DEMO_ACCOUNTS)[number]) => {
    setEmail(account.email);
    setPassword(account.password);
    setError(null);
    toast.message('已填入演示账号', { description: '可直接点击登录' });
  };

  const handleOAuth = (provider: string) => {
    toast.info(`${provider} 登录尚未接入`, {
      description:
        '请在服务端配置 OAuth 应用（Client ID / Secret）并暴露授权回调后，再与此处按钮对接。',
    });
  };

  return (
    <div className="h-full min-h-0 w-full overflow-x-hidden overflow-y-auto overscroll-y-contain font-['Manrope',sans-serif] md:flex">
      {/* 移动端：顶部霓虹全宽 */}
      <div className="md:hidden">
        <NeonColumn className="px-6 py-10">
          <div className="relative z-10">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 text-cyan-300/90">
                <Sparkles className="h-5 w-5 shrink-0 text-cyan-400" aria-hidden />
                <span className="text-xs font-semibold uppercase tracking-[0.2em]">
                  Enterprise workspace
                </span>
              </div>
              <h1 className="font-['Syne',sans-serif] text-3xl font-extrabold tracking-tight">
                <span className="bg-gradient-to-r from-cyan-200 via-white to-fuchsia-200 bg-clip-text text-transparent">
                  SystemHub
                </span>
              </h1>
              <p className="max-w-md text-sm leading-relaxed text-zinc-400">
                企业应用与运营统一入口
              </p>
            </div>
            <LoginSideFeatureList variant="mobile" />
          </div>
        </NeonColumn>
      </div>

      {/* 桌面：左侧霓虹铺满整列（无 max-width 限制） */}
      <aside className="relative hidden min-h-0 w-1/2 shrink-0 lg:w-[46%] md:flex md:flex-col md:self-stretch">
        <NeonColumn className="flex h-full min-h-0 flex-1 flex-col">
          <div className="relative z-10 flex min-h-0 flex-1 flex-col px-10 py-12 lg:px-14 lg:py-16">
            <div className="shrink-0 space-y-6">
              <div className="inline-flex items-center gap-2 text-cyan-300/90">
                <Sparkles className="h-5 w-5 shrink-0 text-cyan-400" aria-hidden />
                <span className="text-xs font-semibold uppercase tracking-[0.2em]">
                  Enterprise workspace
                </span>
              </div>
              <div className="space-y-3">
                <h1 className="font-['Syne',sans-serif] text-4xl font-extrabold tracking-tight lg:text-5xl xl:text-[3.25rem]">
                  <span className="bg-gradient-to-r from-cyan-200 via-white to-fuchsia-200 bg-clip-text text-transparent">
                    SystemHub
                  </span>
                </h1>
                <p className="max-w-md text-base leading-relaxed text-zinc-400">
                  企业应用与运营统一入口
                </p>
              </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col justify-center py-10 lg:py-14">
              <LoginSideFeatureList variant="desktop" />
            </div>

            <div className="pointer-events-none mt-auto shrink-0" aria-hidden>
              <div className="h-px w-full max-w-xs bg-gradient-to-r from-transparent via-cyan-400/45 to-transparent" />
            </div>
          </div>
        </NeonColumn>
      </aside>

      {/* 右侧表单区 */}
      <main className="relative flex min-h-0 flex-1 flex-col justify-center bg-[#f1f2f4] px-5 py-10 sm:px-10 lg:px-16">
        <div
          className="pointer-events-none absolute inset-0 opacity-70"
          aria-hidden
          style={{
            backgroundImage:
              'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(34, 211, 238, 0.12), transparent)',
          }}
        />

        <div className="relative mx-auto w-full max-w-md space-y-8">
          <header
            className="space-y-2 opacity-0 motion-reduce:animate-none motion-reduce:opacity-100 animate-login-fade-up"
            style={{ animationDelay: '60ms' }}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              欢迎回来
            </p>
            <h2 className="font-['Syne',sans-serif] text-3xl font-bold tracking-tight text-slate-900">
              登录 SystemHub
            </h2>
          </header>

          <div
            className="rounded-2xl bg-slate-200/70 p-1 opacity-0 motion-reduce:animate-none motion-reduce:opacity-100 animate-login-fade-up"
            style={{ animationDelay: '100ms' }}
            role="tablist"
            aria-label="登录方式"
          >
            <div className="grid grid-cols-2 gap-1">
              <button
                type="button"
                role="tab"
                aria-selected={authPanel === 'password'}
                className={cn(
                  'rounded-xl py-3 text-sm font-semibold transition-all duration-200',
                  authPanel === 'password'
                    ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/80'
                    : 'text-slate-600 hover:bg-white/50 hover:text-slate-900',
                )}
                onClick={() => {
                  setAuthPanel('password');
                  setError(null);
                }}
              >
                账号密码
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={authPanel === 'oauth'}
                className={cn(
                  'rounded-xl py-3 text-sm font-semibold transition-all duration-200',
                  authPanel === 'oauth'
                    ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/80'
                    : 'text-slate-600 hover:bg-white/50 hover:text-slate-900',
                )}
                onClick={() => {
                  setAuthPanel('oauth');
                  setError(null);
                }}
              >
                第三方登录
              </button>
            </div>
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-800 shadow-sm backdrop-blur-sm"
            >
              {error}
            </div>
          )}

          {authPanel === 'password' ? (
            <form
              onSubmit={handlePasswordLogin}
              className="space-y-5 opacity-0 motion-reduce:animate-none motion-reduce:opacity-100 animate-login-fade-up"
              style={{ animationDelay: '140ms' }}
            >
              <div className="space-y-2">
                <Label htmlFor="login-email">邮箱</Label>
                <Input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11 rounded-xl border-slate-200/90 bg-white/90 shadow-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password">密码</Label>
                <Input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 rounded-xl border-slate-200/90 bg-white/90 shadow-sm"
                />
              </div>

              <div className="rounded-xl border border-slate-200/80 bg-white/50 px-4 py-3">
                <p className="mb-2 text-xs font-medium text-slate-500">
                  演示账号快速填入
                </p>
                <div className="flex flex-wrap gap-2">
                  {DEMO_ACCOUNTS.map((acc) => (
                    <Button
                      key={acc.id}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-lg border-slate-200 bg-white/80 text-slate-700 hover:border-cyan-300/60 hover:bg-cyan-50/40"
                      onClick={() => applyDemo(acc)}
                    >
                      {acc.name}
                    </Button>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="h-11 w-full rounded-xl bg-slate-900 font-semibold text-white shadow-sm transition-all hover:bg-gradient-to-r hover:from-cyan-600 hover:to-fuchsia-600 hover:shadow-[0_12px_40px_-16px_rgba(34,211,238,0.45)] disabled:opacity-60"
              >
                {submitting ? '登录中…' : '登录'}
              </Button>
            </form>
          ) : (
            <div
              className="space-y-4 opacity-0 motion-reduce:animate-none motion-reduce:opacity-100 animate-login-fade-up"
              style={{ animationDelay: '140ms' }}
            >
              <p className="text-sm text-slate-600">
                使用已绑定的第三方账号登录。当前为前端占位，点击将提示接入说明。
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 justify-start gap-3 rounded-xl border-slate-200/90 bg-white/90 text-slate-800 shadow-sm hover:border-emerald-400/40 hover:bg-emerald-50/30"
                  onClick={() => handleOAuth('微信')}
                >
                  <WeChatGlyph className="h-5 w-5 text-emerald-600" />
                  使用微信登录
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 justify-start gap-3 rounded-xl border-slate-200/90 bg-white/90 text-slate-800 shadow-sm hover:border-slate-400/50 hover:bg-slate-50/80"
                  onClick={() => handleOAuth('GitHub')}
                >
                  <Github className="h-5 w-5" />
                  使用 GitHub 登录
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 justify-start gap-3 rounded-xl border-slate-200/90 bg-white/90 text-slate-800 shadow-sm hover:border-blue-300/50 hover:bg-blue-50/40"
                  onClick={() => handleOAuth('Google')}
                >
                  <GoogleGlyph className="h-5 w-5 shrink-0" />
                  使用 Google 登录
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
